// LeadQualificationCompletenessReport.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  buildLeadQualificationCompleteness,
  formatCompletenessPercent,
  resolveLeadQualificationChecklistField,
} from 'collection/reports/lead/leadQualificationCompleteness';

// CSS
import './LeadQualificationCompletenessReport.css';

/**
 * LeadQualificationCompletenessReport
 *
 * Overview widget: template checklist completeness + per-item done rates.
 *
 * @returns {ReactNode}
 */
const LeadQualificationCompletenessReport = ({
  nodes = [],
  fields = [],
  isLoading = false,
  title = 'Qualification',
  description = 'How far leads are through type-level qualification checklist items.',
}) => {
  const { theme } = useTheme();

  const field = useMemo(
    () => resolveLeadQualificationChecklistField(fields),
    [fields],
  );

  const report = useMemo(
    () => buildLeadQualificationCompleteness({ nodes, field }),
    [nodes, field],
  );

  if (!field) {
    return (
      <div className="lead-qualification-report-container">
        <div className="lead-qualification-report-intro">
          <div
            className="lead-qualification-report-title"
            style={{ color: theme.foreground }}
          >
            {title}
          </div>
          <div
            className="lead-qualification-report-description"
            style={{ color: theme.foregroundMuted }}
          >
            {description}
          </div>
        </div>
        <div
          className="lead-qualification-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          Add template items to a checklist field (for example Qualified: Budget, Authority, Need, Timing) to see completion rates.
        </div>
      </div>
    );
  }

  if (isLoading && !report?.leadCount) {
    return (
      <div className="lead-qualification-report-container">
        <div
          className="lead-qualification-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          Loading…
        </div>
      </div>
    );
  }

  if (!report || report.leadCount === 0) {
    return (
      <div className="lead-qualification-report-container">
        <div className="lead-qualification-report-intro">
          <div
            className="lead-qualification-report-title"
            style={{ color: theme.foreground }}
          >
            {title}
          </div>
        </div>
        <div
          className="lead-qualification-report-empty"
          style={{ color: theme.foregroundMuted }}
        >
          No leads yet.
        </div>
      </div>
    );
  }

  return (
    <div className="lead-qualification-report-container">
      <div className="lead-qualification-report-intro">
        <div
          className="lead-qualification-report-title"
          style={{ color: theme.foreground }}
        >
          {title}
        </div>
        <div
          className="lead-qualification-report-description"
          style={{ color: theme.foregroundMuted }}
        >
          {`${report.fieldLabel} · ${report.leadCount} ${report.leadCount === 1 ? 'lead' : 'leads'}`}
        </div>
      </div>

      <div className="lead-qualification-report-summary">
        <div className="lead-qualification-report-metric">
          <div
            className="lead-qualification-report-metric-label"
            style={{ color: theme.foregroundMuted }}
          >
            Avg complete
          </div>
          <div
            className="lead-qualification-report-metric-value"
            style={{ color: theme.foreground }}
          >
            {formatCompletenessPercent(report.avgCompleteness)}
          </div>
        </div>
        <div className="lead-qualification-report-metric">
          <div
            className="lead-qualification-report-metric-label"
            style={{ color: theme.foregroundMuted }}
          >
            Fully qualified
          </div>
          <div
            className="lead-qualification-report-metric-value"
            style={{ color: theme.foreground }}
          >
            {formatCompletenessPercent(report.percentComplete)}
          </div>
          <div
            className="lead-qualification-report-metric-note"
            style={{ color: theme.foregroundMuted }}
          >
            {`${report.completeCount} of ${report.leadCount}`}
          </div>
        </div>
        <div className="lead-qualification-report-metric">
          <div
            className="lead-qualification-report-metric-label"
            style={{ color: theme.foregroundMuted }}
          >
            Criteria
          </div>
          <div
            className="lead-qualification-report-metric-value"
            style={{ color: theme.foreground }}
          >
            {report.items.length}
          </div>
        </div>
      </div>

      <div className="lead-qualification-report-items">
        {report.items.map((item) => (
          <div className="lead-qualification-report-item" key={item.key}>
            <div
              className="lead-qualification-report-item-label"
              style={{ color: theme.foreground }}
            >
              {item.label}
            </div>
            <div
              className="lead-qualification-report-item-pct"
              style={{ color: theme.foregroundMuted }}
            >
              {formatCompletenessPercent(item.percentDone)}
            </div>
            <div className="lead-qualification-report-item-bar">
              <div
                className="lead-qualification-report-item-bar-fill"
                style={{ width: `${Math.round(item.percentDone * 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

LeadQualificationCompletenessReport.displayName = 'LeadQualificationCompletenessReport';

export default LeadQualificationCompletenessReport;
